import { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '@/shared/components/Button/Button'
import IconButton from '@/shared/components/IconButton/IconButton'
import { LAYOUT_COLUMNS, Row, RowLayout } from '../../schema/document'
import {
  canChangeLayout,
  changeRowLayout,
  deleteElement,
  locateElement,
  moveElement,
  setColumnAlign,
  updateElement,
  updateRow
} from '../../utils/documentOps'
import { useWebsiteStore } from '../../store/useWebsiteStore'
import LayoutPicker from '../LayoutPicker/LayoutPicker'
import BackgroundPicker from '../BackgroundPicker/BackgroundPicker'
import PageSettings from './PageSettings'
import { derivePalette } from '../../render/palette'
import { DEFAULT_BRAND_COLOR } from '../../config'
import ElementProperties from './ElementProperties'
import TokenSelect from './TokenSelect'
import styles from './PropertiesPanel.module.scss'

const ALL_LAYOUTS = Object.keys(LAYOUT_COLUMNS) as RowLayout[]

/**
 * The builder's right-hand panel: what is selected, then the whole page.
 *
 * Selection first, because that is what was just clicked. The page's own
 * settings follow in collapsed groups and are always there, which is the point
 * of the panel: before this they appeared only when nothing was selected, so
 * the colours had no findable way in.
 *
 * Six controls at most before "Zaawansowane" (§6 of the brief). The six that
 * stay visible are the ones an owner touches while building a page; the rest are
 * the ones they set once, if ever.
 */
function PropertiesPanel() {
  const { t } = useTranslation()
  const [showAdvanced, setShowAdvanced] = useState(false)

  const draft = useWebsiteStore(s => s.draft)
  const activePageId = useWebsiteStore(s => s.activePageId)
  const language = useWebsiteStore(s => s.editingLanguage)
  const selection = useWebsiteStore(s => s.selection)
  const apply = useWebsiteStore(s => s.apply)
  const previewApply = useWebsiteStore(s => s.previewApply)
  const commitPreview = useWebsiteStore(s => s.commitPreview)
  const select = useWebsiteStore(s => s.select)

  const page = draft.pages.find(item => item.id === activePageId)

  const renderSelection = (): ReactNode => {
    if (!page || !selection) {
      return (
        <p className={`body-small ${styles.hint}`}>
          {t('WEBSITE.PROPS.EMPTY')}
        </p>
      )
    }

    if (selection.kind === 'row') {
      const row = page.blocks.find(item => item.id === selection.id)
      if (!row) return null

      const blockedLayouts = ALL_LAYOUTS.filter(
        layout => !canChangeLayout(row, layout)
      )
      const patch = (values: Partial<Omit<Row, 'id' | 'columns' | 'layout'>>) =>
        apply(doc => updateRow(doc, page.id, row.id, values))

      return (
        <>
          <p className={`title-bold-small ${styles.title}`}>
            {t('WEBSITE.PROPS.ROW')}
          </p>

          <div className={styles.field}>
            <p className={`label-bold-medium ${styles.fieldLabel}`}>
              {t('WEBSITE.PROPS.LAYOUT')}
            </p>
            <LayoutPicker
              value={row.layout}
              disabled={blockedLayouts}
              onPick={layout =>
                apply(doc => changeRowLayout(doc, page.id, row.id, layout))
              }
            />
          </div>

          <TokenSelect
            label={t('WEBSITE.PROPS.PADDING')}
            value={row.paddingY}
            options={['S', 'M', 'L']}
            translate={value => t(`WEBSITE.SIZE.${value}`)}
            onChange={value => patch({ paddingY: value as Row['paddingY'] })}
          />
          <TokenSelect
            label={t('WEBSITE.PROPS.GAP')}
            value={row.gap}
            options={['S', 'M', 'L']}
            translate={value => t(`WEBSITE.SIZE.${value}`)}
            onChange={value => patch({ gap: value as Row['gap'] })}
          />
          <BackgroundPicker
            value={row.background}
            palette={derivePalette(
              draft.theme.brandColor ?? DEFAULT_BRAND_COLOR
            )}
            onChange={background => patch({ background })}
          />

          <Button
            label={t(
              showAdvanced ? 'WEBSITE.PROPS.LESS' : 'WEBSITE.PROPS.MORE'
            )}
            variant="text"
            color="primary"
            textAlignment="left"
            onClick={() => setShowAdvanced(!showAdvanced)}
          />

          {showAdvanced && (
            <>
              <TokenSelect
                label={t('WEBSITE.PROPS.WIDTH')}
                value={row.maxWidth}
                options={['contained', 'full']}
                translate={value => t(`WEBSITE.WIDTH.${value}`)}
                onChange={value =>
                  patch({ maxWidth: value as Row['maxWidth'] })
                }
              />
              <TokenSelect
                label={t('WEBSITE.PROPS.VALIGN')}
                value={row.verticalAlign}
                options={['top', 'center', 'bottom']}
                translate={value => t(`WEBSITE.VALIGN.${value}`)}
                onChange={value =>
                  patch({ verticalAlign: value as Row['verticalAlign'] })
                }
              />
              <TokenSelect
                label={t('WEBSITE.PROPS.MOBILE')}
                value={row.mobileBehavior}
                options={['stack', 'keep2']}
                translate={value => t(`WEBSITE.MOBILE.${value}`)}
                onChange={value =>
                  patch({ mobileBehavior: value as Row['mobileBehavior'] })
                }
              />
            </>
          )}
        </>
      )
    }

    if (selection.kind === 'column') {
      const column = page.blocks
        .flatMap(row => row.columns)
        .find(item => item.id === selection.id)
      if (!column) return null

      return (
        <>
          <p className={`title-bold-small ${styles.title}`}>
            {t('WEBSITE.PROPS.COLUMN')}
          </p>
          <TokenSelect
            label={t('WEBSITE.PROPS.ALIGN')}
            value={column.align}
            options={['left', 'center', 'right']}
            translate={value => t(`WEBSITE.ALIGN.${value}`)}
            onChange={value =>
              apply(doc =>
                setColumnAlign(
                  doc,
                  page.id,
                  column.id,
                  value as 'left' | 'center' | 'right'
                )
              )
            }
          />
        </>
      )
    }

    const located = locateElement(page, selection.id)
    if (!located) return null

    return (
      <>
        <div className={styles.elementHeader}>
          <p className={`title-bold-small ${styles.title}`}>
            {t(`WEBSITE.ELEMENT.${located.element.type}`)}
          </p>
          <div className={styles.elementActions}>
            <IconButton
              icon="las la-arrow-up icon-medium"
              color="primary"
              ariaLabel={t('WEBSITE.ELEMENT_ACTION.UP')}
              onClick={() =>
                apply(doc => moveElement(doc, page.id, located.element.id, -1))
              }
            />
            <IconButton
              icon="las la-arrow-down icon-medium"
              color="primary"
              ariaLabel={t('WEBSITE.ELEMENT_ACTION.DOWN')}
              onClick={() =>
                apply(doc => moveElement(doc, page.id, located.element.id, 1))
              }
            />
            <IconButton
              icon="las la-trash icon-medium"
              color="primary"
              ariaLabel={t('WEBSITE.ELEMENT_ACTION.DELETE')}
              onClick={() => {
                apply(doc => deleteElement(doc, page.id, located.element.id))
                select(null)
              }}
            />
          </div>
        </div>

        <ElementProperties
          element={located.element}
          language={language}
          onChange={patch =>
            apply(doc => updateElement(doc, page.id, located.element.id, patch))
          }
        />
      </>
    )
  }

  return (
    <div className={styles.panel}>
      {renderSelection()}
      <PageSettings
        theme={draft.theme}
        onChange={theme => apply(doc => ({ ...doc, theme }))}
        onPreview={theme => previewApply(doc => ({ ...doc, theme }))}
        onPreviewEnd={commitPreview}
      />
    </div>
  )
}

export default PropertiesPanel
